import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { USDollar, USDollarCents } from "~/functions";
import useReportsStore from "~/store/reportsSectionStore";
import { X } from "lucide-react";

export default function ChartReceivables({ data }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const { chartOfAccountSelected, setChartOfAccountSelected } =
    useReportsStore();
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const handleClick = (data, index) => {
    setActiveIndex(index === activeIndex ? null : index);
    setChartOfAccountSelected(
      data.code === chartOfAccountSelected ? null : data.code
    );
  };

  const handleMouseMove = (state) => {
    if (state && typeof state.activeTooltipIndex === "number") {
      setHoveredIndex(state.activeTooltipIndex);
    } else {
      setHoveredIndex(null);
    }
  };

  const handleMouseLeave = () => setHoveredIndex(null);

  const selectedAccount = useMemo(() => {
    if (!chartOfAccountSelected) return null;
    return data?.byChartOfAccount?.find(
      (item) => item.code === chartOfAccountSelected
    );
  }, [chartOfAccountSelected, data]);

  useEffect(() => {
    if (!chartOfAccountSelected) {
      setActiveIndex(null);
    }
  }, [chartOfAccountSelected]);

  if (!data) return null;

  return (
    <div className="w-full p-2 px-4 border rounded-lg">
      <div className="flex flex-row items-center justify-between mb-4">
        <div className="flex flex-col items-start justify-center">
          <h1 className="text-lg font-bold">Receivables</h1>
          <h1 className="text-sm font-extralight text-gray-500">
            by Chart of Accounts
          </h1>
        </div>
        <div className="flex flex-row items-end justify-center gap-4">
          <div className="text-xl font-bold border-b border-l p-2 rounded-sm w-40">
            <h2 className="text-sm font-light text-gray-500">Total</h2>
            <span>
              {USDollarCents.format(
                data.byChartOfAccount?.reduce(
                  (a, b) => a + Number(b.total || 0),
                  0
                ) || 0
              )}
            </span>
          </div>
        </div>
      </div>
      <div className="w-full relative">
        <ResponsiveContainer width="100%" height={150}>
          <BarChart
            width={150}
            height={40}
            data={data.byChartOfAccount}
            margin={{ top: 0, right: 0, bottom: 16, left: 0 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <Tooltip
              contentStyle={{ backgroundColor: "transparent", border: "none" }}
              wrapperStyle={{
                backgroundColor: "rgba(255,255,255, 0.7)",
                border: "1px solid #ccc",
                borderRadius: "5px",
                pointerEvents: "none",
              }}
              cursor={{ fill: "rgba(64, 61, 138, 0.1)" }}
              labelStyle={{
                display: "none",
              }}
              formatter={(value) => USDollarCents.format(Number(value ?? 0))}
            />
            <CartesianGrid vertical={false} strokeOpacity={0.15} />
            <XAxis dataKey="name" fontSize={12} axisLine={false} tickLine={false} />
            <YAxis
              width={100}
              fontSize={12}
              tickFormatter={(value) => {
                return USDollar.format(value);
              }}
              axisLine={false}
              tickLine={false}
              domain={[0, 'dataMax']}
            />
            <Bar 
              dataKey="total" 
              radius={[8, 8, 8, 8]}
            >
              <LabelList
                dataKey="total"
                isAnimationActive={false}
                content={(props) => {
                  const { x, y, width, height, value } = props;
                  const val = Number(value ?? 0);
                  const isLowBar = height < 30;
                  const yPos = isLowBar ? y - 8 : y + height - 12;
                  return (
                    <text
                      x={x + width / 2}
                      y={yPos}
                      textAnchor="middle"
                      className="text-[13px] font-semibold"
                      fill={isLowBar ? "#111827" : "#FFFFFF"}
                      pointerEvents="none"
                    >
                      {USDollarCents.format(val)}
                    </text>
                  );
                }}
              />
              {data.byChartOfAccount?.map((entry, index) => (
                <Cell
                  cursor="pointer"
                  key={`cell-${index}`}
                  fill={
                    activeIndex === index && chartOfAccountSelected
                      ? "#2E2A72"
                      : "#403D8A"
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div 
          className="absolute inset-0 flex"
          style={{ 
            marginLeft: '100px',
            marginBottom: '16px',
            pointerEvents: 'none'
          }}
        >
          {data.byChartOfAccount?.map((entry, index) => (
            <div
              key={`overlay-${index}`}
              className="flex-1"
              onClick={() => handleClick(entry, index)}
              onMouseEnter={() => setHoveredIndex(index)}
              style={{ 
                background: 'transparent',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            />
          ))}
        </div>
      </div>
      {selectedAccount && (
        <div className="w-full mt-6">
          <div className="flex flex-row items-center justify-between mb-2">
            <div className="flex flex-col items-start justify-center">
              <h2 className="text-base font-semibold">{selectedAccount?.name}</h2>
              <h3 className="text-xs font-extralight text-gray-500">
                Children breakdown (by total)
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setChartOfAccountSelected(null)}
              className="bg-white border text-xs rounded-sm px-3 py-1.5 hover:bg-gray-100 flex flex-row items-center justify-center gap-1"
              title="Close"
            >
              <X size={14} />
              <span>Close</span>
            </button>
          </div>
          {selectedAccount?.children?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={selectedAccount.children}
                margin={{ top: 0, right: 0, bottom: 16, left: 0 }}
              >
                <Tooltip
                  contentStyle={{ backgroundColor: "transparent", border: "none" }}
                  wrapperStyle={{
                    backgroundColor: "rgba(255,255,255, 0.7)",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                  }}
                  cursor={{ fill: "transparent" }}
                  labelFormatter={(label, payload) => payload?.[0]?.payload?.name}
                  formatter={(value, name, { payload }) => [
                    USDollarCents.format(Number(value ?? 0)),
                    `${payload.code}`,
                  ]}
                />
                <CartesianGrid vertical={false} strokeOpacity={0.15} />
                <XAxis
                  dataKey="name"
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  width={100}
                  fontSize={12}
                  tickFormatter={(value) => USDollar.format(value)}
                  axisLine={false}
                  tickLine={false}
                />
                <Bar dataKey="total" radius={[8, 8, 0, 0]}>
                  <LabelList
                    dataKey="total"
                    isAnimationActive={false}
                    content={(props) => {
                      const { x, y, width, height, value } = props;
                      const val = Number(value ?? 0);
                      const isLowBar = height < 30;
                      const yPos = isLowBar ? y - 8 : y + height - 12;
                      return (
                        <text
                          x={x + width / 2}
                          y={yPos}
                          textAnchor="middle"
                          className="text-[12px] font-semibold"
                          fill={isLowBar ? "#111827" : "#FFFFFF"}
                          pointerEvents="none"
                        >
                          {USDollarCents.format(val)}
                        </text>
                      );
                    }}
                  />
                  {(selectedAccount?.children || []).map((entry, index) => (
                    <Cell key={`child-cell-${index}`} fill="#403D8A" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-xs text-gray-500 p-2">No children for this account.</div>
          )}
        </div>
      )}
    </div>
  );
}
